import { useEffect, useState } from 'react';
import { Button } from './ui/button';
import { CheckCircle2, Package, Mail } from 'lucide-react@0.487.0';
import { ThankYouAnimation } from './ThankYouAnimation';

interface PaymentSuccessProps {
  onContinue: () => void;
}

export function PaymentSuccess({ onContinue }: PaymentSuccessProps) {
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showAnimation, setShowAnimation] = useState(true);
  const [showContent, setShowContent] = useState(false);

  useEffect(() => {
    // Get session ID from URL
    const params = new URLSearchParams(window.location.search);
    const id = params.get('session_id');
    setSessionId(id);

    // Animation timeline:
    // 0-2500ms: Animation plays
    // 2500-3500ms: Hold final frame
    // 3500-4000ms: Fade out animation
    // 4000ms: Show success content
    
    // Start fading out animation
    setTimeout(() => {
      setShowAnimation(false);
    }, 3500);

    // Show success content
    setTimeout(() => {
      setShowContent(true);
    }, 4000);
  }, []);

  return (
    <div className="min-h-screen bg-black flex items-center justify-center p-4 relative">
      {/* Fullscreen Animation Overlay */}
      {showAnimation && (
        <div 
          className="fixed inset-0 z-50 bg-black flex items-center justify-center transition-opacity duration-500"
          style={{ opacity: showAnimation ? 1 : 0 }}
        >
          <div className="w-full h-full">
            <ThankYouAnimation />
          </div>
        </div>
      )}

      {/* Success Content */}
      <div 
        className="max-w-2xl w-full transition-opacity duration-500"
        style={{ opacity: showContent ? 1 : 0 }}
      >
        <div className="text-center space-y-8">

          {/* Success Icon */}
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-green-100 rounded-full flex items-center justify-center">
              <CheckCircle2 className="w-12 h-12 text-green-600" />
            </div>
          </div>

          {/* Success Message */}
          <div className="space-y-4">
            <h1 className="tracking-tight text-white">
              Payment Successful!
            </h1>
            <p className="text-neutral-300 max-w-md mx-auto">
              Your fine art print will be shipped directly to you on 
              Hahnemühle Photo Rag premium paper with museum-quality archival standards.
            </p>
          </div>

          {/* Info Cards */}
          <div className="grid md:grid-cols-2 gap-6 max-w-xl mx-auto mt-12">
            <div className="border border-neutral-800 bg-neutral-900 p-6 rounded space-y-3">
              <div className="flex justify-center">
                <Mail className="w-8 h-8 text-white" />
              </div>
              <h3 className="tracking-wide text-white">Confirmation Sent</h3>
              <p className="text-sm text-neutral-400">
                Order confirmation has been sent to your email with all details
              </p>
            </div>

            <div className="border border-neutral-800 bg-neutral-900 p-6 rounded space-y-3">
              <div className="flex justify-center">
                <Package className="w-8 h-8 text-white" />
              </div>
              <h3 className="tracking-wide text-white">Shipping</h3>
              <p className="text-sm text-neutral-400">
                Prints are shipped directly from Creative Hub print center to you worldwide
              </p>
            </div>
          </div>

          {/* Quality Guarantee */}
          <div className="bg-neutral-900 border border-neutral-800 p-6 rounded max-w-xl mx-auto mt-8">
            <p className="text-sm text-white tracking-wide mb-2">
              Quality Guarantee
            </p>
            <p className="text-sm text-neutral-300">
              If your print arrives damaged, send an email to{' '}
              <a 
                href="mailto:keyiscreation@gmail.com" 
                className="text-white underline hover:text-neutral-300"
              >
                keyiscreation@gmail.com
              </a>
              {' '}and we will send a new print completely free.
            </p>
          </div>

          {/* Session ID (for reference) */}
          {sessionId && (
            <p className="text-xs text-neutral-600 mt-8">
              Session ID: {sessionId}
            </p>
          )}

          {/* Continue Button */}
          <div className="mt-12">
            <Button
              onClick={onContinue}
              className="bg-white text-black hover:bg-neutral-200 px-12"
            >
              Continue Shopping
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default PaymentSuccess;